import { useMemo } from "react";
import { useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useAppSelector } from "redux/hooks";
import { selectScreen } from "redux/appSlice";
import { selectLayout, selectSideNavCollpased } from "redux/preferencesSlice";
import { hasAccessTo, isAdmin } from "layouts/Config";
import { useFeatures } from "hooks/use-feature";
import type { RootState } from "utils/store";
import type { TourEvalContext } from "../types/tour.types";

export const useTourEvalContext = (): TourEvalContext => {
  const location = useLocation();
  const isMobile = useAppSelector(selectScreen);
  const layout = useAppSelector(selectLayout);
  const sideNavCollapsed = useAppSelector(selectSideNavCollpased);
  const resources = useAppSelector((state: RootState) => state.auth.resources);
  const { has, loaded } = useFeatures();
  const hasSideNav = useMediaQuery((theme: Theme) =>
    theme.breakpoints.up("lg"),
  );

  return useMemo<TourEvalContext>(
    () => ({
      pathname: location.pathname,
      isMobile,
      layout,
      sideNavCollapsed,
      hasSideNav,
      isAdmin: isAdmin(),
      can: (resource: string, permission: string) =>
        hasAccessTo(resource, permission),
      hasFeature: has,
      featuresLoaded: loaded,
    }),
    // `resources` is deliberate. `hasAccessTo` and `isAdmin` read the store
    // directly instead of taking arguments, so nothing else listed here
    // changes when permissions land — subscribing to `resources` above and
    // depending on it here is what rebuilds the context at that point. The
    // rule only sees that it is unused inside the callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      location.pathname,
      isMobile,
      layout,
      sideNavCollapsed,
      hasSideNav,
      resources,
      has,
      loaded,
    ],
  );
};

export default useTourEvalContext;
